// Formateadores y aritmética de presentación del panel de consumo de IA
// (pestaña "IA" de Reportes). Viven fuera del componente para poder testearlos
// sin montar React — misma razón que format.js.
//
// es-CL en todos: coma decimal y punto de miles, igual que el resto del portal.
// Un costo en USD se escribe con esas mismas reglas ("US$1.234,5678") porque
// mezclar dos convenciones en columnas vecinas de la misma tabla se lee como
// error de tipeo, no como precisión.

import { localYMD } from "./format";

// 4 decimales fijos: el costo de una respuesta ronda el centavo de centavo, y
// redondear a 2 haría que casi toda la tabla mostrara "0,00".
const nfUsd = new Intl.NumberFormat("es-CL", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const nfInt = new Intl.NumberFormat("es-CL");
// Compacto de tokens: máximo 1 decimal. En el tramo "k" el valor escalado nunca
// llega a 1000 (ver umbrales), así que la agrupación de miles solo aparece en
// el tramo M, donde sí ayuda ("1.234M").
const nfCompact = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 });

const K_MIN = 1000;
// Corte de k a M y no en 1.000.000: con 1 decimal, nfCompact redondea 999.950k
// (y todo lo que siga hasta el millón) a "1.000", que mezclaría el separador de
// miles con el sufijo ("1.000k"). Desde acá se dice "1M", que es lo mismo dicho
// sin ambigüedad.
const M_MIN = 999_950;

/**
 * Costo estimado en USD con 4 decimales. null/undefined/NaN → raya: "sin
 * precio de referencia" es un dato legítimo del contrato (el modelo del agente
 * no tiene tarifa cargada), no un cero.
 *
 * A diferencia de los demás formateadores, acá el tipo se exige ESTRICTO: la
 * cadena vacía coerciona a 0 y pintaría "US$0,0000", que afirma que el consumo
 * fue gratis. El contrato manda number|null.
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatUsd(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `US$${nfUsd.format(value)}`;
}

/**
 * Conteo de tokens compacto: "542", "1,2k", "3,4M". Se usa en celdas y KPIs
 * donde el número exacto (hasta 7 dígitos) rompería el ancho de la tabla; el
 * valor exacto sigue disponible en el título de la celda.
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatTokens(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  // Los tokens son enteros: redondear primero elimina los bordes de fracción
  // (999,5 → 1000 → "1k") y evita que la rama exacta imprima "1.000" para 999,9.
  // El `|| 0` normaliza el -0 de Math.round(-0,4), que se pintaría como "-0".
  const n = Math.round(Number(value)) || 0;
  const abs = Math.abs(n);
  if (abs >= M_MIN) return `${nfCompact.format(n / 1e6)}M`;
  if (abs >= K_MIN) return `${nfCompact.format(n / 1e3)}k`;
  return nfInt.format(n);
}

/**
 * Fracción consumida del presupuesto diario (0 = nada, 1 = justo en el tope,
 * >1 = excedido). Devuelve la razón SIN recortar: quien dibuja la barra recorta
 * el ancho, pero el exceso (>1) es la señal que pinta la alerta.
 * `null` cuando no hay límite (0, ausente o basura): "sin límite" no es "0%
 * usado", y pintar una barra vacía diría lo contrario de lo que pasa.
 * @param {number|null|undefined} used
 * @param {number|null|undefined} limit
 * @returns {number|null}
 */
export function budgetRatio(used, limit) {
  const max = Number(limit);
  if (limit === null || limit === undefined || !Number.isFinite(max) || max <= 0) return null;
  const value = Number(used);
  // Uso negativo o ausente cuenta como 0: no hay consumo que reportar.
  const safeUsed = Number.isFinite(value) && value > 0 ? value : 0;
  return safeUsed / max;
}

/**
 * Rellena con ceros los días sin actividad de la serie diaria de tokens. El
 * backend manda solo los días con consumo; sin relleno la línea "saltaría" los
 * días de silencio y sugeriría continuidad donde no la hubo (mismo criterio que
 * fillDays en format.js). La ventana son los últimos `days` días LOCALES,
 * terminando hoy.
 * @param {Array<{day: string, tokens_in: number, tokens_out: number, actions: number}>} series
 * @param {number} days
 * @param {Date} [today] - inyectable para testear el borde de fin de mes.
 * @returns {Array<{day: string, tokens_in: number, tokens_out: number, actions: number}>}
 */
export function fillUsageDays(series, days, today = new Date()) {
  const rows = Array.isArray(series) ? series : [];
  const count = Number(days);
  if (!Number.isFinite(count) || count <= 0) return rows;

  const byDay = new Map(rows.map((row) => [row?.day, row]));
  const cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  cursor.setDate(cursor.getDate() - (count - 1));

  const out = [];
  // Tope defensivo: `days` viene validado a 7/30, pero un valor corrupto no debe
  // colgar el render en un bucle largo.
  for (let i = 0; i < count && i < 100; i += 1) {
    const key = localYMD(cursor);
    const row = byDay.get(key);
    out.push({
      day: key,
      tokens_in: Number(row?.tokens_in) || 0,
      tokens_out: Number(row?.tokens_out) || 0,
      actions: Number(row?.actions) || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}
